import React, { useState } from 'react';
import { Modal, TextInput, Button, Stack, Group, NumberInput, Select, Alert } from '@mantine/core';
import { useForm } from '@mantine/form';
import { IconCheck, IconX } from '@tabler/icons-react';
import { showNotification } from '@mantine/notifications';
import { onBoardUsersToOrgAdmin } from 'api';
import { useUser } from 'stores/useUser';

const AddUserModal = ({ opened, onClose, onSubmit, departmentName }) => {
  const { user } = useUser();
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const form = useForm({
    initialValues: {
      email: '',
      first_name: '',
      last_name: '',
      title: '',
      amount: '',
      currencyType: 'USD',
      managerEmail: '',
    },
    validate: {
      email: (value) => {
        if (!value) return 'Email is required';
        return /^\S+@\S+$/.test(value) ? null : 'Invalid email format';
      },
      first_name: (value) => (!value ? 'First name is required' : null),
      last_name: (value) => (!value ? 'Last name is required' : null),
      title: (value) => (!value ? 'Title is required' : null),
      amount: (value) => {
        if (!value) return 'Hourly rate is required';
        return value > 0 ? null : 'Hourly rate must be greater than 0';
      },
      managerEmail: (value) => {
        if (value && !/^\S+@\S+$/.test(value)) {
          return 'Invalid email format';
        }
        return null;
      },
    },
  });

  const handleSubmit = async (values) => {
    setIsSubmitting(true);
    try {
      const userData = {
        ...values,
        amount: Math.ceil(values.amount), // Round up to nearest integer as per user preference
        department: departmentName,
        managerEmail: values.managerEmail || '',
      };
      
      await onBoardUsersToOrgAdmin(user.subscription.organization_id, [userData]);
      
      showNotification({
        title: 'Success',
        message: 'User added successfully!',
        color: 'green',
        icon: <IconCheck size={16} />,
      });
      
      form.reset();
      onClose();
      
      // Call the onSubmit callback to refresh the user list
      if (onSubmit) {
        onSubmit(userData);
      }
    } catch (error) {
      console.error('Error adding user:', error);
      showNotification({
        title: 'Error',
        message: error.message || 'Failed to add user. Please try again.',
        color: 'red',
        icon: <IconX size={16} />,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    form.reset();
    onClose();
  };

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title={`Add User to ${departmentName}`}
      size="md"
      centered
    >
      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Stack gap="md">
          <TextInput
            label="Email"
            placeholder="user@company.com"
            required
            {...form.getInputProps('email')}
          />
          
          <Group grow>
            <TextInput
              label="First Name"
              placeholder="John"
              required
              {...form.getInputProps('first_name')}
            />
            <TextInput
              label="Last Name"
              placeholder="Doe"
              required
              {...form.getInputProps('last_name')}
            />
          </Group>

          <TextInput
            label="Job Title"
            placeholder="Software Engineer"
            required
            {...form.getInputProps('title')}
          />

          <Group grow>
            <Select
              label="Currency"
              data={[
                { value: 'USD', label: 'USD ($)' },
                { value: 'EUR', label: 'EUR (€)' },
                { value: 'INR', label: 'INR (₹)' },
              ]}
              {...form.getInputProps('currencyType')}
            />
            <NumberInput
              label="Hourly Rate"
              placeholder="50"
              min={0}
              required
              thousandSeparator=","
              prefix="$"
              {...form.getInputProps('amount')}
            />
          </Group>

          <TextInput
            label="Manager Email (Optional)"
            placeholder="manager@company.com"
            {...form.getInputProps('managerEmail')}
          />

          <Group justify="flex-end" mt="md">
            <Button variant="subtle" onClick={handleClose} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button type="submit" variant="filled" loading={isSubmitting}>
              Add User
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
};

export default AddUserModal;
